/**
 * 전역 네트워크 활동/재시도 상태 (중앙 오버레이 스피너 + 30초 타임아웃 재시도).
 *  - axios 인터셉터가 begin()/end()/presentRetry() 를 호출하고, GlobalLoader 가 subscribe() 로 구독.
 *  - 스피너는 300ms 지연 후에만 표시(빠른 요청 깜빡임 방지).
 *  - 타임아웃/네트워크오류 시 presentRetry() 가 사용자의 '다시 시도'/'닫기' 입력을 기다린다.
 *    인터셉터는 '다시 시도' 면 같은 요청을 재실행 → 원 호출자(await 중)에게 결과가 그대로 전달됨.
 */
let inFlight = 0;
let visible = false;
let showTimer = null;
let retryState = null; // { message, resolve } | null
const listeners = new Set();

function snapshot() {
  return {
    loading: visible && inFlight > 0 && !retryState,
    // busy: 오버레이 표시(300ms 지연)와 무관하게 '요청 진행 중' 원신호 —
    //  빈 상태 문구(EmptyView)가 로딩 중 비쳐 보이지 않게 게이트하는 용도.
    busy: inFlight > 0 || !!retryState,
    retry: retryState ? { message: retryState.message } : null,
  };
}
function emit() {
  const s = snapshot();
  listeners.forEach((fn) => fn(s));
}

export function subscribe(fn) {
  listeners.add(fn);
  fn(snapshot());
  return () => listeners.delete(fn);
}
export function getState() { return snapshot(); }

export function begin() {
  inFlight += 1;
  if (inFlight === 1) {
    emit(); // busy 즉시 통지(빈 상태 문구 선제 숨김) — 스피너는 아래 300ms 지연 유지
    if (!showTimer && !visible) {
      showTimer = setTimeout(() => { showTimer = null; visible = true; emit(); }, 300);
    }
  }
}
export function end() {
  inFlight = Math.max(0, inFlight - 1);
  if (inFlight === 0) {
    if (showTimer) { clearTimeout(showTimer); showTimer = null; }
    visible = false;
    emit();
  }
}

// 타임아웃/네트워크오류 → 재시도 오버레이. 사용자가 '다시 시도'/'닫기' 누를 때까지 Promise 대기.
//  반환: 'retry' | 'cancel'
export function presentRetry(message) {
  return new Promise((resolve) => {
    // 재시도 대기 중엔 스피너 감춤(retry 오버레이가 대신 뜸)
    if (showTimer) { clearTimeout(showTimer); showTimer = null; }
    visible = false;
    retryState = { message: message || "연결이 지연되고 있어요. 다시 시도할까요?", resolve };
    emit();
  });
}
export function resolveRetry(action) {
  if (!retryState) return;
  const resolve = retryState.resolve;
  retryState = null;
  emit();
  resolve(action);
}

// 요청이 타임아웃/네트워크오류인지 판정(HTTP 4xx/5xx 는 각 화면이 처리 → 오버레이 대상 아님).
export function isRetryableError(error) {
  if (!error || error.code === "ERR_CANCELED" || error.name === "CanceledError" || error.name === "AbortError") return false;
  const timeout = error.code === "ECONNABORTED" || /timeout/i.test(error.message || "");
  const network = !error.response && !!error.request; // 응답 자체가 없음(연결 실패 등)
  return timeout || network;
}
